import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import HeaderDashboard from './components/HeaderDashboard/HeaderDashboard'
import useDashboardPage from './hooks/useDashboardPage'
import s from './styles/DashboardPage.module.scss'
import { observer } from 'mobx-react-lite'
// import AssignmentsSection from './components/AssignmentsSection/AssignmentsSection'
// import SessionsSection from './components/SessionsSection/SessionsSection'
import FeatureDiscoveryCarousel from './components/FeatureDiscoveryCarousel/FeatureDiscoveryCarousel'
// import BookSessionModal from 'widgets/BookSessionModal/BookSessionModal'
// import { studentsStore } from 'pages/Students/model/studentsStore'
import HeaderContent from './components/HeaderContent/HeaderContent'
import TodaysPrioritySection from './components/TodaysPrioritySection/TodaysPrioritySection'
import KodaInsightsSection from './components/KodaInsightsSection/KodaInsightsSection'
import CurriculumSection from './components/CurriculumSection/CurriculumSection'
import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg'
import { ReactComponent as AppIcon } from 'assets/icons/app.svg'
import { ReactComponent as DockIcon } from 'assets/icons/dock_pen.svg'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'

const toISO = d => {
	const pad = v => String(v).padStart(2, '0')
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// DEV-ONLY fixtures for note-taker QA. Remove when real backend wiring lands.
// const DEV_NOTE_TAKER_FIXTURES = {
// 	students: [
// 		{ id: 'fixture-sarah-chen', name: 'Sarah Chen', grade: 12, section: 'A' },
// 		{ id: 'fixture-ahmed-khalil', name: 'Ahmed Khalil', grade: 11, section: 'B' },
// 		{ id: 'fixture-priya-mehta', name: 'Priya Mehta', grade: 12, section: 'C' },
// 	],
// 	sessions: [
// 		{
// 			id: 'fixture-session-1',
// 			startsInMin: 0,
// 			studentName: 'Sarah Chen',
// 			grade: 12,
// 			timeLabel: '10:30',
// 			scheduledDateISO: toISO(new Date()),
// 			topic: 'Country Finder',
// 		},
// 		{
// 			id: 'fixture-session-2',
// 			startsInMin: 210,
// 			studentName: 'Ahmed Khalil',
// 			grade: 11,
// 			timeLabel: '14:00',
// 			scheduledDateISO: toISO(new Date()),
// 			topic: 'Essay Review',
// 		},
// 		{
// 			id: 'fixture-session-3',
// 			startsInMin: 360,
// 			studentName: 'Priya Mehta',
// 			grade: 12,
// 			timeLabel: '16:30',
// 			scheduledDateISO: toISO(new Date()),
// 			topic: 'LoR Prep',
// 		},
// 	],
// }

export default observer(function DashboardPage() {
	// const [isBookSessionOpen, setIsBookSessionOpen] = useState(false)
	const [pendingReviewTotal, setPendingReviewTotal] = useState(0)
	const [studentRepliesCount, setStudentRepliesCount] = useState(0)
	const [priorityTab, setPriorityTab] = useState(null)
	const priorityRef = useRef(null)
	const location = useLocation()
	const { rows, sessions, dashboardStats } = useDashboardPage()

	useEffect(() => {
		ensureStudentsLoaded().catch(error => {
			console.error('Failed to prepare student list for note taker:', error)
		})
	}, [])

	useEffect(() => {
		const state = location.state
		if (!state?.priorityTab && !state?.scrollToPriority) return

		if (state.priorityTab) {
			setPriorityTab(state.priorityTab)
		}

		if (state.scrollToPriority) {
			requestAnimationFrame(() => {
				priorityRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				})
			})
		}
	}, [location.key, location.state])

	const sessionsForUi = useMemo(() => {
		const realSessions = Array.isArray(sessions) ? sessions : []
		return realSessions.length ? realSessions : []
	}, [sessions])
	const todaySessionsCount = useMemo(() => {
		const today = toISO(new Date())
		return sessionsForUi.filter(s => s?.scheduledDateISO === today).length
	}, [sessionsForUi])
	const summaryCards = useMemo(
		() => [
			{
				id: 'students',
				label: 'Students',
				value:
					dashboardStats?.total_students ??
					dashboardStats?.active_students_this_week ??
					0,
				icon: <PeopleIcon />,
			},
			{
				id: 'apps',
				label: 'Active Apps',
				value: dashboardStats?.active_applications ?? 0,
				icon: <AppIcon />,
			},
			{
				id: 'pending',
				label: 'Pending',
				value: pendingReviewTotal,
				icon: <DockIcon />,
			},
			{
				id: 'sessions',
				label: 'Sessions Today',
				value: todaySessionsCount,
				icon: <CalendarIcon />,
			},
		],
		[dashboardStats, pendingReviewTotal, todaySessionsCount],
	)
	const handleJumpToPriority = tab => {
		setPriorityTab(tab)
		priorityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}

	return (
		<div className={s.page}>
			<HeaderDashboard />
			<div className={s.content}>
				<HeaderContent
					rows={rows}
					sessions={sessionsForUi}
					pendingReviewTotal={pendingReviewTotal}
					studentRepliesCount={studentRepliesCount}
					className={s.contentHeader}
					onSummaryClick={handleJumpToPriority}
				/>

				<div className={s.topRow}>
					<div className={s.summaryGrid}>
						{summaryCards.map(card => (
							<div key={card.id} className={s.summaryCard}>
								<span className={s.summaryIcon}>{card.icon}</span>
								<div className={s.summaryInner}>
									<span className={s.summaryValue}>{card.value}</span>
									<span className={s.summaryLabel}>{card.label}</span>
								</div>
							</div>
						))}
					</div>

					<div className={s.featureDiscovery}>
						{/* <SessionsSection
							items={sessionsForUi}
							onStart={() => setIsBookSessionOpen(true)}
						/> */}
						<FeatureDiscoveryCarousel />
					</div>
				</div>

				<KodaInsightsSection />

				<TodaysPrioritySection
					onPendingTotalChange={setPendingReviewTotal}
					onFeedbackTotalChange={setStudentRepliesCount}
					forcedTab={priorityTab}
					sectionRef={priorityRef}
				/>

				<CurriculumSection />
			</div>
			{/* <BookSessionModal
				isOpen={isBookSessionOpen}
				onClose={() => {
					setIsBookSessionOpen(false)
					refetchSessions()
				}}
				selectedStudents={studentsStore.selectedStudents || []}
				savedLists={studentsStore.savedLists || []}
				onSelectList={() => {}}
			/> */}
		</div>
	)
})
